/**
 * The Activate wizard's destination-type list — **derived**, not declared.
 *
 * This file used to carry its own hardcoded array: the type union, the display names, and a copy of
 * `supportedAccountDefinitions` that the server also held in
 * `ACTIVATION_ACCOUNT_DEFS_BY_DESTINATION`. Two lists, one fact, no mechanism keeping them equal —
 * a destination could be offered here that the server had no spec for, or carry an account filter
 * that disagreed with the one actually applied.
 *
 * Both now come from `@server/activation-destinations`, aliased into the SPA the same way as
 * `@server/postgres` / `@server/clickhouse`. Adding a destination is an entry in that registry plus
 * a mark in the icon registry — nothing in this file changes.
 *
 * The account-definition list is deliberately NOT re-exposed here: the client never used it (the
 * step-2 account filter runs server-side, in `listRsActivationAccounts`), so carrying it was pure
 * drift surface.
 */
import {
  ACTIVATION_DESTINATION_TYPE_NAMES,
  type ActivationDestinationSpec,
  type ActivationDestinationType as ActivationDestinationTypeKey,
  activationSpec,
  type AudienceTypeAxis,
  audienceTypeAxis,
  extraMappingsSpec,
  type FarSideSpec,
  requiresPlatformAccount,
} from '@server/activation-destinations';
import type * as React from 'react';

import { destinationTypeIcon } from '../icons/destinationTypes';

/** The destination-definition name, e.g. `FB_CUSTOM_AUDIENCE`. Widens with the registry. */
export type ActivationDestinationTypeName = ActivationDestinationTypeKey;

/** What a wizard step needs to render one destination type. */
export interface ActivationDestinationType {
  name: ActivationDestinationTypeName;
  displayName: string;
  /** Short platform label for far-side copy ("Meta Ads", "TikTok Ads"). */
  platform: string;
  /**
   * Whether this type has a platform account beneath the RudderStack credential. `false` for
   * Iterable, whose lists hang off the API-key account alone — the wizard shows no picker and the
   * audience list is reachable immediately.
   */
  requiresPlatformAccount: boolean;
  /**
   * Label for the platform-account input (Meta ad account / TikTok advertiser).
   * Empty when {@link requiresPlatformAccount} is false — there is no input to label.
   */
  platformAccountLabel: string;
  /**
   * The `user` / `company` axis, for a destination whose audiences come in kinds. `null` for the
   * types that have no such split — the wizard renders no selector for them.
   */
  audienceTypes: AudienceTypeAxis | null;
  /** The second mapping table's copy, or `null` for a destination with only one table. */
  extraMappings: ActivationDestinationSpec['extraMappings'] | null;
  /**
   * What this destination's sync writes into: an audience over there — one the customer already has,
   * or (for a destination that declares `create`, ACT2-1082) one created on Enable — or, for Braze, a
   * named custom attribute the user chooses, because Braze has no audience object.
   */
  farSide: FarSideSpec;
  /** Add-row label on Map identifiers. */
  mappingAddLabel: string;
  /** One line on how this destination matches, under Map identifiers; `null` for none (ACT2-1287). */
  mappingHint: string | null;
  /** The fields the hash question governs, from the spec's `hashedFields`; `null` when it declares none (ACT2-1287). */
  hashedFields: readonly string[] | null;
  /** Resolved from the shared icon registry by `name` — never declared per type. */
  Icon: React.ComponentType<{ className?: string }>;
}

/**
 * The mark is *resolved*, not declared — by the same `definitionName` key the workspace-wide
 * activations list resolves with. A type whose brand mark is added (or corrected) in the registry
 * cannot then disagree with itself between the wizard and the list, because there is only one lookup.
 */
export const ACTIVATION_DESTINATION_TYPES: readonly ActivationDestinationType[] = ACTIVATION_DESTINATION_TYPE_NAMES.map(
  (name) => {
    // `activationSpec`, not the `as const` literal: the accessor returns the declared spec type, on
    // which `platformAccountLabel` is optional. Reading the literal union instead would fail to
    // compile the moment one type omits an optional field — which Iterable does.
    const spec = activationSpec(name);
    return {
      name,
      displayName: spec.displayName,
      platform: spec.platform,
      requiresPlatformAccount: requiresPlatformAccount(name),
      audienceTypes: audienceTypeAxis(name),
      extraMappings: extraMappingsSpec(name),
      farSide: spec.farSide,
      platformAccountLabel: spec.platformAccountLabel ?? '',
      mappingAddLabel: spec.mappingAddLabel,
      mappingHint: spec.mappingHint ?? null,
      hashedFields: spec.hashedFields ?? null,
      Icon: destinationTypeIcon(name).Icon,
    };
  },
);
